import throttle from "../../utils/throttle";
import isFullyInViewPort from "../../utils/isFullyInViewport";

// Players object to store YouTube players
const fluidPlayers = {};

document.addEventListener("DOMContentLoaded", () => {
  const fluidItems = document.querySelectorAll(".fluid-item");

  // Only initialize if fluid items exist
  if (fluidItems.length > 0) {
    // Load YouTube IFrame API if not already loaded by heroVideo.js
    if (!window.YT) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      // Ensure onYouTubeIframeAPIReady doesn't overwrite existing function
      const originalOnYouTubeIframeAPIReady =
        window.onYouTubeIframeAPIReady || function () {};

      window.onYouTubeIframeAPIReady = function () {
        originalOnYouTubeIframeAPIReady();
        initializeFluidPlayers();
      };
    } else {
      // If YouTube API is already loaded
      window.addEventListener("load", initializeFluidPlayers);
    }

    function initializeFluidPlayers() {
      document.querySelectorAll(".fluid-item iframe").forEach((iframe) => {
        const videoId = iframe.getAttribute("data-youtube-id");
        if (videoId && !fluidPlayers[videoId]) {
          fluidPlayers[videoId] = new YT.Player(iframe.id, {
            events: {
              onReady: onFluidPlayerReady,
              onStateChange: onFluidPlayerStateChange,
            },
            playerVars: {
              autoplay: 1,
              loop: 1,
              playlist: videoId,
              mute: 1,
              controls: 0,
              showinfo: 0,
              rel: 0,
              modestbranding: 1,
              playsinline: 1,
            },
          });
        }
      });
    }

    function onFluidPlayerReady(event) {
      // Start playing when ready
      const player = event.target;

      // Set video to fill container
      player.setSize("100%", "100%");

      // Try to set to highest available quality for better coverage
      try {
        player.setPlaybackQuality("hd1080");
      } catch (e) {
        // If setting quality fails, continue anyway
        console.log("Could not set video quality", e);
      }

      player.playVideo();
    }

    function onFluidPlayerStateChange(event) {
      const iframe = event.target.getIframe();
      const fluidItem = iframe.closest(".fluid-item");

      // Update poster visibility based on player state
      if (event.data === YT.PlayerState.PLAYING) {
        fluidItem.querySelector(".fluid-item-poster").style.opacity = "0";
        fluidItem.classList.remove("paused");
      } else if (
        event.data === YT.PlayerState.PAUSED ||
        event.data === YT.PlayerState.ENDED
      ) {
        fluidItem.querySelector(".fluid-item-poster").style.opacity = "1";
        fluidItem.classList.add("paused");
      }
    }

    function playIfInView(fluidItem) {
      if (window.innerWidth >= 1000) return;

      const videoId = fluidItem
        .querySelector("iframe")
        .getAttribute("data-youtube-id");
      const player = fluidPlayers[videoId];

      const shouldPlay = isFullyInViewPort(fluidItem);

      if (
        player &&
        typeof player.playVideo === "function" &&
        typeof player.pauseVideo === "function"
      ) {
        if (shouldPlay) {
          player.playVideo();
          fluidItem.classList.remove("paused");
        } else {
          player.pauseVideo();
          fluidItem.classList.add("paused");
        }
      }
    }

    function handleVideos() {
      const isDesktop = window.innerWidth >= 1000;

      fluidItems.forEach((item) => {
        const iframe = item.querySelector("iframe");
        const videoId = iframe?.getAttribute("data-youtube-id");
        const player = videoId ? fluidPlayers[videoId] : null;

        /* Remove existing event listeners */
        if (item._handleMouseEnter) {
          item.removeEventListener("mouseenter", item._handleMouseEnter);
        }
        if (item._handleMouseLeave) {
          item.removeEventListener("mouseleave", item._handleMouseLeave);
        }

        if (isDesktop && player) {
          // Pause the video initially
          if (typeof player.pauseVideo === "function") {
            player.pauseVideo();
          }
          item.classList.add("paused");

          // Define event handlers
          item._handleMouseEnter = () => {
            if (typeof player.playVideo === "function") {
              player.playVideo();
            }
            item.classList.remove("paused");
          };
          item._handleMouseLeave = () => {
            if (typeof player.pauseVideo === "function") {
              player.pauseVideo();
            }
            item.classList.add("paused");
          };

          // Add event listeners
          item.addEventListener("mouseenter", item._handleMouseEnter);
          item.addEventListener("mouseleave", item._handleMouseLeave);
        } else {
          // On mobile, play if video is in viewport
          playIfInView(item);
        }
      });
    }

    // Attach a separate throttled function for each fluid-item
    const throttledFunctions = Array.from(fluidItems).map((item) =>
      throttle(() => playIfInView(item), 100)
    );

    // Wait for YouTube API and players to be ready
    window.addEventListener("load", () => {
      setTimeout(() => {
        // Run on page load
        handleVideos();
      }, 1000); // Give time for YouTube players to initialize
    });

    // Attach the scroll event listener
    window.addEventListener("scroll", () => {
      throttledFunctions.forEach((fn) => fn());
    });

    // Use throttled version of handleVideos for the resize event
    window.addEventListener("resize", throttle(handleVideos, 100));
  }
});
